import * as React from "react"
import {echo} from "app/components/websocketeer";

import WatermarkMarkerCommon, { ObjectsType } from "products/components/watermark-marker-common"
import { getInpaintedImageBlob, godProjectId, isGodModeOn, projectId, saveInpaintingObjects } from "app/helpers/api"
import { ID } from "app/types/app-types"
import { AxiosResponse } from "axios"
import { InpaintingGroupType, PositionType } from "products/types"
import { getObjectsSizeAndPosition, recalculateObjects } from "products/components/render-objects-helper";

const {sqrt} = Math

type WebSocketMessage = {
    is_alt: boolean
    image_or_alt_image_id: number
    image_or_alt_image_hash?: string
    product_id: number
    project_id: number
    status: string
    base64_image_src: string
}

interface Props {
    productId: ID
    templateId: ID
    altTemplateId: ID
    isAlt: boolean
    altLevel: number
    imageOrAltUrl: string
    imageOrAltId: ID
    imageOrAltHash: string
    width: number
    height: number
    close: Function
    updateInpaintingGroups: Function
    selectedGroup: InpaintingGroupType
    changeGroupId: Function
}

interface State {
    isWaitingForResult: boolean
    imageBlob: string
    isSaving: boolean
    groupId: ID
}

class InpaintingMarker extends React.Component<Props, State> {
    constructor(props: Props) {
        super(props)

        this.state = {
            isSaving: null,
            isWaitingForResult: false,
            imageBlob: null,
            groupId: props.selectedGroup?.id,
        }

        const actualProjectId = isGodModeOn ? godProjectId : projectId

        echo.channel(`private-project.${actualProjectId}`)
            .listen('.inpainting.started', (event) => this.handleProcessStarted(event))

        echo.channel(`private-project.${actualProjectId}`)
            .listen('.inpainting.done', (event) => this.handleProcessDone(event))
    }

    clearImageBlob = () => {
        this.setState({
            imageBlob: null,
            isWaitingForResult: false,
        })
    }

    handleProcessStarted = (message: WebSocketMessage) => {
        const { productId, imageOrAltId } = this.props
        if (message.product_id === productId && message.image_or_alt_image_id === imageOrAltId) {
            this.setState({ isWaitingForResult: true })
        }
    }

    handleProcessDone = (message: WebSocketMessage) => {
        const { productId, imageOrAltId, imageOrAltHash, isAlt } = this.props

        if ((message.image_or_alt_image_id === imageOrAltId || message.product_id === productId)  || (message.image_or_alt_image_hash && message.image_or_alt_image_hash === imageOrAltHash)) {
            this.setState({
                imageBlob: message.base64_image_src,
                isWaitingForResult: false
            })

            this.refreshResultImage()
        }
    }

    refreshResultImage = () => {
        const { productId, imageOrAltId, selectedGroup } = this.props

        if (productId && imageOrAltId && selectedGroup?.id) {
            console.debug(`start refreshing product ${productId} image ${imageOrAltId} group ${selectedGroup.id}`)

            getInpaintedImageBlob(productId, imageOrAltId, selectedGroup?.id)
                .then((response: AxiosResponse<any>) => {
                    this.setState({
                        imageBlob: response.data.imageBlob,
                        isWaitingForResult: false
                    })
                })
                .catch(error => {
                    console.debug('bad response from refresh image', error.response)
                })
        } else {
            console.debug(`Cannot refresh result product ${productId}, image ${imageOrAltId}, group ${selectedGroup?.id}`)
        }
    }

    componentDidMount() {
        if (this.props.selectedGroup?.user_set_objects) {
            this.refreshResultImage()
        }
    }

    saveObjects = ( objects: ObjectsType, png: string, isCloseOnSave: boolean) => {
        const { selectedGroup, imageOrAltUrl, imageOrAltId, productId, templateId, altTemplateId, width, height, isAlt } = this.props
        const { isSaving } = this.state

        if (isSaving) {
            return;
        }

        this.setState({
            isSaving: true,
            isWaitingForResult: true
        })

        const isGroupDefined = typeof selectedGroup !== 'undefined'

        let preferredPos: PositionType = 'bottomleft'
        if (!isGroupDefined && objects && objects.length > 0) {
            const sizeAndPosition = getObjectsSizeAndPosition(objects)

            //get nearest corner:
            const cornerPoints = {
                topleft: [0, 0],
                topcenter: [width / 2, 0],
                topright: [width, 0],
                leftcenter: [0, height / 2],
                center: [width / 2, height / 2],
                rightcenter: [width, height / 2],
                bottomleft: [0, height],
                bottomcenter: [width / 2, height],
                bottomright: [width, height],
            }

            let minDistance = null
            let minDistanceKey = null

            Object.keys(cornerPoints).forEach((k: PositionType) => {
                const d = sqrt(
                    (cornerPoints[k][0] - sizeAndPosition.middleX) * (cornerPoints[k][0] - sizeAndPosition.middleX) +
                    (cornerPoints[k][1] - sizeAndPosition.middleY) * (cornerPoints[k][1] - sizeAndPosition.middleY)
                )

                if (minDistance === null || d < minDistance) {
                    minDistance = d
                    minDistanceKey = k
                }
            })

            preferredPos = minDistanceKey
        }

        const position = isGroupDefined ? selectedGroup?.pos_relative_to : preferredPos
        const scaleObjects = isGroupDefined ? !!selectedGroup?.scale_objects : true

        saveInpaintingObjects(isAlt, selectedGroup?.id, objects, png, width, height, productId, templateId, altTemplateId, imageOrAltId, imageOrAltUrl, position, scaleObjects)
            .then((response: AxiosResponse<{group: InpaintingGroupType }>) => {
                const newGroupId = response.data?.group?.id
                if (newGroupId && newGroupId !== selectedGroup?.id) {
                    this.props.changeGroupId(newGroupId)
                }
            })
            .catch(e => {
                console.error('cannot save group image mask', e)
            })
            .finally(() => {
                this.props.updateInpaintingGroups()
                this.setState({ isSaving: false })
                if (isCloseOnSave) {
                    this.props.close()
                }
            })
    }

    render() {
        const { imageOrAltUrl, imageOrAltHash, isAlt, imageOrAltId, productId, width, height, selectedGroup, altLevel } = this.props
        const { imageBlob } = this.state

        const recalculatedObjects: string = recalculateObjects(selectedGroup, width, height)

        return <WatermarkMarkerCommon
            markerType={'inpainting-group'}
            saveObjects={this.saveObjects}
            clearImageBlob={this.clearImageBlob}
            closeModal={this.props.close}
            is_alt={isAlt}
            altLevel={altLevel}
            image_id={imageOrAltId}
            image_url={imageOrAltUrl}
            image_url_hash={null}
            image_width={width}
            image_height={height}
            image_hash={imageOrAltHash}
            group_id={selectedGroup?.id}
            product_id={productId}
            user_set_objects={recalculatedObjects}
            mask_hash={selectedGroup?.mask_hash}
            imageBlob={imageBlob ? imageBlob : imageOrAltUrl}
            isWaitingForResult={this.state.isWaitingForResult || this.state.isSaving}
            showClose={true}
            posRelativeTo={selectedGroup?.pos_relative_to}
            scaleObjects={selectedGroup?.scale_objects}
        />
    }
}

export default InpaintingMarker
